import { Link } from "react-router-dom";
import { ArrowUpRight, Phone, Home as HomeIcon, ShieldCheck, Wrench, Clock4, Quote } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import ServiceCard from "@/components/ServiceCard";
import useSeo from "@/hooks/useSeo";
import { SERVICES, TESTIMONIALS, PHONE_DISPLAY, PHONE_TEL, PROCESS_STEPS, HERO_IMAGE, HOME_INTERIOR_IMAGE, TOOLBELT_IMAGE, SERVICE_BY_SLUG } from "@/lib/data";

const POPULAR_SLUGS = ["renovations", "plumbing", "electrical", "painting", "handyman", "general-repairs", "landscaping", "pool-services"];

const HOMEOWNER_FAQS = [
  { q: "I just need one small fix — is that worth your time?", a: "Absolutely. Half our work is the small stuff — a dripping tap, a sticking door, a broken tile. We're set up for short jobs as much as full renovations." },
  { q: "What if my job needs more than one trade?", a: "Even better. That's the whole point. One quote covers every trade involved, one coordinator runs the schedule, and you only ever ring one number." },
  { q: "Do you handle insurance work?", a: "Yes — we work with most major Australian insurers for repair claims and can provide compliant quotes, photos and reports." },
  { q: "How do quotes work for a home?", a: "Most home jobs get a written, fixed-price quote inside one business day. Bigger jobs get a free on-site walkthrough first." },
];

export default function Homeowners() {
  useSeo({
    title: "For Homeowners — RSR Australia",
    description: "Renovations, repairs, plumbing, electrical, painting and more for Australian homes. One coordinator, one quote, every trade covered.",
  });
  const popular = SERVICES.filter(s => POPULAR_SLUGS.includes(s.slug));
  const residentialTestimonials = TESTIMONIALS.filter(t => t.role.toLowerCase().includes("homeowner"));

  return (
    <>
      {/* Hero — split with photo block */}
      <section data-testid="homeowners-hero" className="border-b border-border">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 pt-20 pb-16 lg:pt-28 lg:pb-24 grid grid-cols-12 gap-8 lg:gap-12 items-center">
          <div className="col-span-12 lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="rule-accent" />
              <span className="overline">For homeowners</span>
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-[88px] tracking-tighter leading-[0.96] font-extrabold">
              Property care <br className="hidden sm:block" /> for homes. <span className="text-accent">Without the call-around.</span>
            </h1>
            <p className="mt-8 max-w-xl text-base sm:text-lg text-muted-foreground">
              One coordinator. One written quote. Every trade your home might ever need — handled. No more chasing four numbers for one job.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact" data-testid="homeowners-hero-quote" className="group inline-flex items-center gap-3 bg-accent text-white px-7 py-4 font-semibold text-sm hover:bg-accent/90 transition-colors">
                Get a free quote <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <a href={`tel:${PHONE_TEL}`} data-testid="homeowners-hero-call" className="inline-flex items-center gap-3 border border-foreground/25 hover:border-foreground px-7 py-4 font-semibold text-sm transition-colors">
                <Phone className="w-4 h-4" /> {PHONE_DISPLAY}
              </a>
            </div>
            <div className="mt-10 flex items-center gap-8">
              <div>
                <div className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter leading-none">14</div>
                <div className="mt-2 text-[11px] uppercase tracking-[0.22em] text-muted-foreground">Trades on call</div>
              </div>
              <div className="h-10 w-px bg-border" />
              <div>
                <div className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter leading-none">1 day</div>
                <div className="mt-2 text-[11px] uppercase tracking-[0.22em] text-muted-foreground">Typical quote turnaround</div>
              </div>
              <div className="h-10 w-px bg-border hidden sm:block" />
              <div className="hidden sm:block">
                <div className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter leading-none">7</div>
                <div className="mt-2 text-[11px] uppercase tracking-[0.22em] text-muted-foreground">Days a week</div>
              </div>
            </div>
          </div>
          <div className="col-span-12 lg:col-span-5">
            <div className="relative">
              <div className="absolute -top-3 -left-3 right-6 bottom-10 border border-accent/40 hidden sm:block" />
              <div className="relative aspect-[4/5] overflow-hidden border border-border bg-secondary">
                <img src={HOME_INTERIOR_IMAGE} alt="A bright, well-kept Australian home interior" className="w-full h-full object-cover duotone" />
              </div>
              <div className="absolute -bottom-5 -right-5 bg-foreground text-background p-5 max-w-[260px] hidden sm:block">
                <div className="text-[10px] font-bold tracking-[0.28em] uppercase" style={{ color: "#7AB3FF" }}>For Australian homes</div>
                <div className="mt-2 font-display text-lg font-bold leading-tight">From a sticking door to a full reno — handled.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The homeowner problem */}
      <section className="py-20 sm:py-24">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12">
          <div className="grid grid-cols-12 gap-10 mb-12">
            <div className="col-span-12 lg:col-span-6">
              <p className="overline">The honest truth</p>
              <h2 className="mt-3 font-display text-4xl sm:text-5xl tracking-tighter leading-[1.02] font-extrabold">
                Most home jobs <br /> don't go to one trade.
              </h2>
            </div>
            <div className="col-span-12 lg:col-span-5 lg:col-start-8 flex items-end">
              <p className="text-base text-muted-foreground">A "quick bathroom fix" is usually a plumber, a tiler and a sparkie. A "small reno" is six trades and four schedules. Co-ordinating that is a second job. We take it off you.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 border-t border-l border-border">
            {[
              { t: "Four phone calls", b: "You ring around for a plumber, a tiler, a sparkie and a painter. Two never call back.", img: UNS("1521791136064-7986c2920216") },
              { t: "Three quotes", b: "Each trade quotes separately, and nobody agrees on what's actually involved.", img: UNS("1454165804606-c3d57bc86b40") },
              { t: "Two months wait", b: "By the time everyone's free at the same time, the job's been sitting half-done for weeks.", img: UNS("1486325212027-8081e485255e") },
            ].map((b, i) => (
              <div key={i} data-testid={`homeowners-pain-${i}`} className="relative bg-card border-r border-b border-border overflow-hidden flex flex-col">
                <div className="relative aspect-[16/10] overflow-hidden bg-secondary">
                  <img src={b.img} alt="" className="absolute inset-0 w-full h-full object-cover duotone opacity-60" />
                  <div className="absolute inset-0 bg-gradient-to-br from-foreground/30 via-foreground/10 to-transparent" />
                  <div className="absolute top-4 left-4 font-display text-5xl font-extrabold text-white drop-shadow-md leading-none tabular-nums">0{i + 1}</div>
                </div>
                <div className="p-7 lg:p-8 flex flex-col gap-3 flex-1">
                  <h3 className="font-display text-xl font-bold tracking-tight">{b.t}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{b.b}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Full-bleed image strip */}
      <section className="relative h-[40vh] sm:h-[50vh] overflow-hidden">
        <img src={HERO_IMAGE} alt="Tradesperson on site" className="absolute inset-0 w-full h-full object-cover duotone" />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground/80 via-foreground/40 to-transparent" />
        <div className="absolute inset-0 flex items-center">
          <div className="mx-auto max-w-[1400px] w-full px-5 sm:px-8 lg:px-12">
            <div className="max-w-2xl text-white">
              <p className="text-[11px] font-bold tracking-[0.28em] uppercase" style={{ color: "#7AB3FF" }}>One number. Every job.</p>
              <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02] font-extrabold">
                Same hands that quote, <span className="text-accent">finish the job.</span>
              </h2>
            </div>
          </div>
        </div>
      </section>

      {/* What homeowners get */}
      <section className="py-20 sm:py-24 border-t border-border bg-card">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-5">
            <div className="relative mb-8 lg:mb-0">
              <div className="aspect-[4/5] overflow-hidden border border-border bg-secondary">
                <img src={TOOLBELT_IMAGE} alt="Trade tools and hardware close-up" className="w-full h-full object-cover duotone" />
              </div>
              <div className="hidden lg:flex absolute -bottom-5 -left-5 right-12 bg-accent text-white px-6 py-5 items-center gap-4">
                <ShieldCheck className="w-7 h-7 flex-shrink-0" strokeWidth={1.7} />
                <div>
                  <div className="text-[10px] font-bold tracking-[0.28em] uppercase opacity-80">Workmanship-backed</div>
                  <div className="font-display text-base font-bold leading-tight">If it's not right, we come back.</div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-span-12 lg:col-span-7">
            <p className="overline">What you get</p>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl tracking-tighter leading-[1.02] font-extrabold">
              Everything under <br /> one written quote.
            </h2>
            <p className="mt-6 text-base text-muted-foreground max-w-md mb-8">
              Every job, big or small, gets the same treatment: clear quote, one coordinator, on time, no surprises on the invoice.
            </p>
            <div className="space-y-px">
              {[
                { icon: HomeIcon, t: "One coordinator across every trade", b: "You speak to the same person from quote to clean-up. They own the schedule." },
                { icon: ShieldCheck, t: "Insured & licensed work, always", b: "Every trade on the job carries the relevant Australian licence and full public liability cover." },
                { icon: Wrench, t: "Workmanship-backed guarantee", b: "If something isn't right after we leave, we come back and put it right — in writing." },
                { icon: Clock4, t: "Booked when it suits you", b: "Weekday, weekend or after-hours where possible. We confirm the crew the day before." },
              ].map((b, i) => (
                <div key={i} data-testid={`homeowners-benefit-${i}`} className="flex items-start gap-5 bg-background border border-border p-6">
                  <b.icon className="w-6 h-6 text-accent flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                  <div>
                    <h3 className="font-display text-lg font-bold tracking-tight">{b.t}</h3>
                    <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{b.b}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Popular services */}
      <section className="py-20 sm:py-24 border-t border-border">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <p className="overline">Most-booked at home</p>
              <h2 className="mt-2 font-display text-3xl sm:text-4xl font-extrabold tracking-tighter">Popular services</h2>
            </div>
            <Link to="/services" className="text-sm font-semibold nav-link inline-flex items-center gap-2">All 14 services <ArrowUpRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-t border-l border-border">
            {popular.map(s => <ServiceCard key={s.slug} service={s} testIdPrefix="homeowners-service" />)}
          </div>
        </div>
      </section>

      {/* Big quote pull */}
      {residentialTestimonials[0] && (
        <section className="py-20 sm:py-24 border-t border-border bg-foreground text-background relative overflow-hidden">
          <div className="absolute right-0 top-0 bottom-0 w-1/2 hidden lg:block">
            <img src={SERVICE_BY_SLUG.renovations.image} alt="" className="w-full h-full object-cover duotone opacity-40" />
            <div className="absolute inset-0 bg-gradient-to-l from-transparent via-foreground/60 to-foreground" />
          </div>
          <div className="relative mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 grid grid-cols-12 gap-10">
            <div className="col-span-12 lg:col-span-8">
              <Quote className="w-10 h-10 text-accent" strokeWidth={1.5} />
              <blockquote className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-[1.1] tracking-tighter text-white">
                "{residentialTestimonials[0].quote}"
              </blockquote>
              <div className="mt-8 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-accent flex items-center justify-center text-white font-display font-bold text-lg">
                  {residentialTestimonials[0].name.charAt(0)}
                </div>
                <div>
                  <div className="text-base font-semibold">{residentialTestimonials[0].name}</div>
                  <div className="text-xs text-background/60">{residentialTestimonials[0].role}</div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Process with image */}
      <section className="py-20 sm:py-24 border-t border-border">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12">
          <div className="grid grid-cols-12 gap-10 mb-12">
            <div className="col-span-12 lg:col-span-5">
              <p className="overline">How a home job works</p>
              <h2 className="mt-3 font-display text-4xl sm:text-5xl tracking-tighter leading-[1.02] font-extrabold">Four steps. <br /> No runaround.</h2>
            </div>
            <div className="col-span-12 lg:col-span-7">
              <div className="relative aspect-[16/8] overflow-hidden border border-border bg-secondary">
                <img src={SERVICE_BY_SLUG.painting.image} alt="" className="w-full h-full object-cover duotone" />
                <div className="absolute inset-0 bg-gradient-to-tr from-foreground/40 to-transparent" />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-l border-border">
            {PROCESS_STEPS.map((step, i) => (
              <div key={i} className="bg-card border-r border-b border-border p-7 lg:p-8 flex flex-col gap-4 min-h-[220px]">
                <div className="font-display text-5xl font-extrabold text-accent leading-none tabular-nums">{step.n}</div>
                <div className="mt-auto">
                  <h3 className="font-display text-xl font-bold tracking-tight">{step.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{step.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 sm:py-24 border-t border-border bg-card">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-4">
            <p className="overline">Homeowner FAQ</p>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl tracking-tighter leading-[1.02] font-extrabold">A few common questions.</h2>
          </div>
          <div className="col-span-12 lg:col-span-8">
            <Accordion type="single" collapsible className="w-full">
              {HOMEOWNER_FAQS.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-b border-border">
                  <AccordionTrigger className="text-left font-display text-lg sm:text-xl font-semibold tracking-tight hover:no-underline hover:text-accent py-6">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-base text-muted-foreground leading-relaxed pb-6 max-w-2xl">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA with image */}
      <section className="py-20">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12">
          <div className="relative overflow-hidden bg-foreground text-background">
            <img src={SERVICE_BY_SLUG.renovations.image} alt="" className="absolute inset-0 w-full h-full object-cover duotone opacity-25" />
            <div className="absolute inset-0 bg-gradient-to-r from-foreground via-foreground/85 to-foreground/30" />
            <div className="relative p-8 lg:p-14 grid grid-cols-12 gap-8 items-center">
              <div className="col-span-12 lg:col-span-8">
                <p className="text-[11px] font-bold tracking-[0.28em] uppercase" style={{ color: "#7AB3FF" }}>Ready when you are</p>
                <h3 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-extrabold leading-[1.05]">
                  Sort the list. Reclaim your weekends.
                </h3>
                <p className="mt-3 text-sm text-background/75 max-w-xl">Most jobs get a written quote inside one business day. Tell us what's on your list and we'll come back with a number.</p>
              </div>
              <div className="col-span-12 lg:col-span-4 flex flex-col gap-3">
                <Link to="/contact" data-testid="homeowners-cta-quote" className="inline-flex items-center justify-between gap-3 bg-accent text-white px-6 py-4 font-semibold text-sm hover:bg-accent/90 transition-colors">
                  Get a free quote <ArrowUpRight className="w-4 h-4" />
                </Link>
                <a href={`tel:${PHONE_TEL}`} data-testid="homeowners-cta-call" className="inline-flex items-center justify-between gap-3 border border-white/30 hover:border-white px-6 py-4 font-semibold text-sm text-white transition-colors">
                  <span className="inline-flex items-center gap-2"><Phone className="w-4 h-4" /> {PHONE_DISPLAY}</span>
                  <ArrowUpRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

const UNS = (id, w = 1400) =>
  `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=${w}&q=75`;
